/**
 * Sonnenstand.
 *
 * Grundlage fuer zwei Dinge: die Strahlung auf die geneigte Flaeche (Kapitel K)
 * und den Schattenwurf (Kapitel N). Beide brauchen dieselbe Rechnung, deshalb
 * steht sie einmal hier.
 *
 * Verfahren nach dem ueblichen Ansatz mit Deklination, Zeitgleichung und
 * wahrer Ortszeit. Genauigkeit rund ein Zehntelgrad — mehr als genug fuer eine
 * Ertragsabschaetzung und fuer die Frage, ob ein Baum ein Dach verschattet.
 */
import { bogenInGrad, gradInBogen, normiereGrad } from './typen.js';
/** Tag im Jahr, 1 bis 365. */
export function tagImJahr(monat, tag) {
    const tageBisMonat = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    return (tageBisMonat[monat - 1] ?? 0) + tag;
}
/** Deklination der Sonne in Grad. */
export function deklination(tagNummer) {
    return 23.45 * Math.sin(gradInBogen((360 / 365) * (tagNummer + 284)));
}
/** Zeitgleichung in Minuten — die Abweichung der Sonnenuhr von der mittleren Zeit. */
export function zeitgleichung(tagNummer) {
    const b = gradInBogen((360 / 364) * (tagNummer - 81));
    return 9.87 * Math.sin(2 * b) - 7.53 * Math.cos(b) - 1.5 * Math.sin(b);
}
/**
 * Sonnenstand fuer Ort, Tag und wahre Ortszeit.
 *
 * @param stunde Wahre Ortszeit in Stunden (12 = Sonnenhoechststand).
 */
export function sonnenstandZurOrtszeit(breiteGrad, tagNummer, stunde) {
    const breite = gradInBogen(breiteGrad);
    const dek = gradInBogen(deklination(tagNummer));
    // Stundenwinkel: 15 Grad je Stunde, mittags null.
    const stundenwinkel = gradInBogen(15 * (stunde - 12));
    const sinHoehe = Math.sin(breite) * Math.sin(dek) + Math.cos(breite) * Math.cos(dek) * Math.cos(stundenwinkel);
    const hoehe = Math.asin(Math.max(-1, Math.min(1, sinHoehe)));
    // Azimut, gemessen von Nord ueber Ost — dieselbe Zaehlweise wie beim Dach.
    // Mittags kommt hier 180 heraus, morgens rund 100. Wer das Vorzeichen oder
    // den Bezug verschiebt, dreht die halbe Ertragsrechnung um.
    const y = Math.sin(stundenwinkel) * Math.cos(dek);
    const x = Math.sin(breite) * Math.cos(dek) * Math.cos(stundenwinkel) - Math.cos(breite) * Math.sin(dek);
    const azimut = Math.atan2(y, x);
    return {
        hoeheGrad: bogenInGrad(hoehe),
        azimutGrad: normiereGrad(bogenInGrad(azimut) + 180),
    };
}
/**
 * Sonnenstand fuer buergerliche Zeit (MEZ bzw. MESZ), wie sie auf der Uhr steht.
 *
 * @param stunde Uhrzeit in Stunden.
 * @param sommerzeit true, wenn die Uhr eine Stunde vorgestellt ist.
 */
export function sonnenstand(breiteGrad, laengeGrad, tagNummer, stunde, sommerzeit = false) {
    // Mitteleuropaeische Zeit bezieht sich auf 15 Grad Ost.
    const bezugslaenge = 15;
    const ortszeit = stunde -
        (sommerzeit ? 1 : 0) +
        (laengeGrad - bezugslaenge) / 15 +
        zeitgleichung(tagNummer) / 60;
    return sonnenstandZurOrtszeit(breiteGrad, tagNummer, ortszeit);
}
/** Wann geht die Sonne auf und unter? Wahre Ortszeit, in Stunden. */
export function tageslaenge(breiteGrad, tagNummer) {
    const breite = gradInBogen(breiteGrad);
    const dek = gradInBogen(deklination(tagNummer));
    const wert = -Math.tan(breite) * Math.tan(dek);
    // Polartag und Polarnacht — in unseren Breiten nie, aber der Code soll nicht
    // stillschweigend Unsinn liefern.
    if (wert <= -1)
        return { aufgang: 0, untergang: 24, stunden: 24 };
    if (wert >= 1)
        return { aufgang: 12, untergang: 12, stunden: 0 };
    const halbeTageslaenge = bogenInGrad(Math.acos(wert)) / 15;
    return {
        aufgang: 12 - halbeTageslaenge,
        untergang: 12 + halbeTageslaenge,
        stunden: 2 * halbeTageslaenge,
    };
}
/**
 * Einfallswinkel der direkten Strahlung auf eine geneigte Flaeche.
 * Rueckgabe ist der Kosinus — negativ heisst: die Sonne steht hinter der Flaeche.
 */
export function kosinusEinfallswinkel(stand, neigungGrad, azimutGrad) {
    const hoehe = gradInBogen(stand.hoeheGrad);
    const neigung = gradInBogen(neigungGrad);
    const richtungsunterschied = gradInBogen(stand.azimutGrad - azimutGrad);
    return (Math.sin(hoehe) * Math.cos(neigung) +
        Math.cos(hoehe) * Math.sin(neigung) * Math.cos(richtungsunterschied));
}
//# sourceMappingURL=sonne.js.map